// Module Gestion Clients - Super Admin
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiPlus, FiEdit, FiEye, FiKey, FiDollarSign,
  FiCheck, FiX, FiClock, FiSearch, FiFilter
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Types
interface Client {
  id: string;
  name: string;
  contactName: string;
  email: string;
  phone: string;
  registrationDate: string;
  subscriptionPlan: 'starter' | 'professional' | 'premium' | 'enterprise';
  subscriptionStatus: 'active' | 'trial' | 'expired' | 'suspended';
  users: number;
  maxUsers: number;
  licenseKey: string;
  monthlyPrice: number;
  expiryDate: string;
  lastActivity: string;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const AddButton = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }
`;

const FiltersBar = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 250px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const FilterSelect = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: ${theme.colors.surface};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.surface};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface}50;
  }
`;

const TableHeader = styled.th`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  text-align: left;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  white-space: nowrap;
`;

const TableCell = styled.td`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const ClientName = styled.div`
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ClientContact = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const PlanBadge = styled.div<{ plan: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  text-transform: uppercase;

  ${props => {
    switch (props.plan) {
      case 'enterprise':
        return `
          background: ${theme.colors.primary}20;
          color: ${theme.colors.primary};
        `;
      case 'premium':
        return `
          background: ${theme.colors.info}20;
          color: ${theme.colors.info};
        `;
      case 'professional':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'starter':
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text}80;
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};

  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'trial':
        return `
          background: ${theme.colors.info}20;
          color: ${theme.colors.info};
        `;
      case 'expired':
        return `
          background: ${theme.colors.error}20;
          color: ${theme.colors.error};
        `;
      case 'suspended':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const UsageBar = styled.div`
  width: 100%;
  max-width: 120px;
`;

const UsageBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${theme.spacing.xs};
`;

const UsageBarFill = styled.div<{ percentage: number }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => {
    if (props.percentage >= 90) return theme.colors.error;
    if (props.percentage >= 70) return theme.colors.warning;
    return theme.colors.success;
  }};
  transition: width 0.3s ease;
`;

const UsageText = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'success' }>`
  padding: ${theme.spacing.sm};
  background: transparent;
  border: 1px solid ${props => {
    if (props.variant === 'primary') return theme.colors.primary;
    if (props.variant === 'success') return theme.colors.success;
    return theme.colors.text + '40';
  }};
  color: ${props => {
    if (props.variant === 'primary') return theme.colors.primary;
    if (props.variant === 'success') return theme.colors.success;
    return theme.colors.text;
  }};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => {
      if (props.variant === 'primary') return theme.colors.primary + '10';
      if (props.variant === 'success') return theme.colors.success + '10';
      return theme.colors.surface;
    }};
  }

  svg {
    font-size: 16px;
  }
`;

export const ClientManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data
  const clients: Client[] = [
    {
      id: '1',
      name: 'Office HLM Paris',
      contactName: 'Marie Dubois',
      email: 'marie.dubois@hlmparis.fr',
      phone: '+33 1 23 45 67 89',
      registrationDate: '2023-06-15',
      subscriptionPlan: 'enterprise',
      subscriptionStatus: 'active',
      users: 45,
      maxUsers: 50,
      licenseKey: 'ENT-2023-PARIS-4F7E9A',
      monthlyPrice: 1290,
      expiryDate: '2025-06-15',
      lastActivity: '2024-01-20T14:30:00'
    },
    {
      id: '2',
      name: 'Habitat Social Lyon',
      contactName: 'Pierre Martin',
      email: 'p.martin@habitat-lyon.fr',
      phone: '+33 4 78 90 12 34',
      registrationDate: '2023-09-20',
      subscriptionPlan: 'premium',
      subscriptionStatus: 'active',
      users: 28,
      maxUsers: 30,
      licenseKey: 'PRE-2023-LYON-8B2C4D',
      monthlyPrice: 890,
      expiryDate: '2024-09-20',
      lastActivity: '2024-01-20T13:15:00'
    },
    {
      id: '3',
      name: 'Logement Social Marseille',
      contactName: 'Sophie Bernard',
      email: 'contact@lsmarseille.fr',
      phone: '+33 4 91 23 45 67',
      registrationDate: '2024-01-05',
      subscriptionPlan: 'professional',
      subscriptionStatus: 'trial',
      users: 12,
      maxUsers: 20,
      licenseKey: 'PRO-2024-MARS-3A9E7F',
      monthlyPrice: 590,
      expiryDate: '2024-02-05',
      lastActivity: '2024-01-19T16:45:00'
    },
    {
      id: '4',
      name: 'Immobilier Social Toulouse',
      contactName: 'Jean Dupont',
      email: 'j.dupont@istoulouse.fr',
      phone: '+33 5 61 12 34 56',
      registrationDate: '2022-11-10',
      subscriptionPlan: 'premium',
      subscriptionStatus: 'expired',
      users: 25,
      maxUsers: 30,
      licenseKey: 'PRE-2022-TOUL-9D4E1B',
      monthlyPrice: 890,
      expiryDate: '2024-01-10',
      lastActivity: '2024-01-08T10:20:00'
    },
    {
      id: '5',
      name: 'HLM Bordeaux',
      contactName: 'Isabelle Petit',
      email: 'i.petit@hlmbordeaux.fr',
      phone: '+33 5 56 78 90 12',
      registrationDate: '2023-08-01',
      subscriptionPlan: 'starter',
      subscriptionStatus: 'active',
      users: 8,
      maxUsers: 10,
      licenseKey: 'STA-2023-BORD-6C8F2E',
      monthlyPrice: 290,
      expiryDate: '2024-08-01',
      lastActivity: '2024-01-18T09:30:00'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <FiCheck />;
      case 'trial': return <FiClock />;
      case 'expired': return <FiX />;
      case 'suspended': return <FiX />;
      default: return null;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'trial': return 'Essai';
      case 'expired': return 'Expiré';
      case 'suspended': return 'Suspendu';
      default: return status;
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderLeft>
            <Title>Gestion des Clients</Title>
            <Subtitle>{clients.length} clients enregistrés</Subtitle>
          </HeaderLeft>
          <AddButton onClick={() => navigate('/superadmin/clients/new')}>
            <FiPlus /> Nouveau Client
          </AddButton>
        </Header>

        <FiltersBar>
          <SearchInput
            type="text"
            placeholder="Rechercher un client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <FilterSelect value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)}>
            <option value="all">Tous les plans</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="premium">Premium</option>
            <option value="enterprise">Enterprise</option>
          </FilterSelect>

          <FilterSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="trial">Essai</option>
            <option value="expired">Expiré</option>
            <option value="suspended">Suspendu</option>
          </FilterSelect>
        </FiltersBar>

        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Client</TableHeader>
                <TableHeader>Plan</TableHeader>
                <TableHeader>Statut</TableHeader>
                <TableHeader>Utilisateurs</TableHeader>
                <TableHeader>Clé Licence</TableHeader>
                <TableHeader>Prix/mois</TableHeader>
                <TableHeader>Expiration</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {clients.map(client => {
                const usagePercentage = (client.users / client.maxUsers) * 100;
                return (
                  <TableRow key={client.id}>
                    <TableCell>
                      <ClientName>{client.name}</ClientName>
                      <ClientContact>{client.contactName} • {client.email}</ClientContact>
                    </TableCell>

                    <TableCell>
                      <PlanBadge plan={client.subscriptionPlan}>
                        {client.subscriptionPlan}
                      </PlanBadge>
                    </TableCell>

                    <TableCell>
                      <StatusBadge status={client.subscriptionStatus}>
                        {getStatusIcon(client.subscriptionStatus)}
                        {getStatusLabel(client.subscriptionStatus)}
                      </StatusBadge>
                    </TableCell>

                    <TableCell>
                      <UsageBar>
                        <UsageBarContainer>
                          <UsageBarFill percentage={usagePercentage} />
                        </UsageBarContainer>
                        <UsageText>
                          {client.users}/{client.maxUsers} utilisateurs
                        </UsageText>
                      </UsageBar>
                    </TableCell>

                    <TableCell>
                      <code style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.primary }}>
                        {client.licenseKey}
                      </code>
                    </TableCell>

                    <TableCell>
                      <strong style={{ color: theme.colors.success }}>
                        {client.monthlyPrice} FCFA
                      </strong>
                    </TableCell>

                    <TableCell>
                      {new Date(client.expiryDate).toLocaleDateString('fr-FR')}
                    </TableCell>

                    <TableCell>
                      <ActionButtons>
                        <ActionButton
                          variant="primary"
                          onClick={() => navigate(`/superadmin/clients/${client.id}`)}
                          title="Voir détails"
                        >
                          <FiEye />
                        </ActionButton>
                        <ActionButton
                          onClick={() => navigate(`/superadmin/clients/${client.id}`)}
                          title="Modifier"
                        >
                          <FiEdit />
                        </ActionButton>
                        <ActionButton
                          variant="success"
                          onClick={() => navigate(`/superadmin/licenses/generate?client=${client.id}`)}
                          title="Gérer licence"
                        >
                          <FiKey />
                        </ActionButton>
                      </ActionButtons>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </Container>
    </DashboardLayout>
  );
};
